/**
 * External
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import getL from 'lodash-es/get';
import isEmptyL from 'lodash-es/isEmpty';
import indexOfL from 'lodash-es/indexOf';

/**
 * Actions
 */
import { updateActionOrigin, updatePlaySource as updatePlaySourceAction } from '../../state/player/actions';
import { setHistoryPathName as setHistoryPathNameAction } from '../../state/history/actions';
import { setSearchInteraction as setSearchInteractionAction } from '../../state/search/searchSlice';

/**
 * Other
 */
import { backendService } from '../../services';
import {
  INTERACTION_PLAY,
  INTERACTION_PDP,
  INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_LIVE,
  INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_VOD,
  redirectToPdp,
  redirectToPlayer,
  redirectToApp,
  redirectToLoginPage,
  useLearnActionClick,
  LANDER_URL,
  PATHS,
  getUserDetailsToken,
  PLAY_SOURCE_PAGE,
} from '../../utils';
import { history } from '../../state/configureStore';
import { useCastActions, useIsCastConnected } from '../chromecastConnectionBar';
import { useIsResponsive } from '../../hooks/useIsResponsive';

function AssetToPlay({
  allowPlay,
  asset: {
    assetId, mainAssetId = '', actionOrigin = '', channelId = '', playSource = '', startTime = '', guideId = '', type, vmsId = '',
    shouldDisplayLockIcon = false, source = '',
  },
  children,
  className,
  fromEpg,
  fromEpgFilterId,
  fromEpgRedirectionIndex,
  isLive,
  isSpan,
  isUserLoggedIn,
  onClick,
  shouldCallLearnAction,
  path,
  redirectToDetailsFlag,
  replace,
  searchTerm,
  setHistoryPathName,
  setSearchInteraction,
  showSearch,
  style,
  updatePlaySource,
  userCountry,
}) {
  const dispatch = useDispatch();
  const { msoProvider, userType } = getUserDetailsToken();
  const { country } = useParams() || {};
  const location = useLocation();
  const storeCode = (userCountry || country || '').toLowerCase();
  const isChromecastConnected = useIsCastConnected();
  const isMobile = useIsResponsive('(max-width: 1024px)');
  const { loadMedia } = useCastActions();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const sourceFromUrl = queryParams.get('source');
  const sourceToUse = source || sourceFromUrl;

  const handleClick = async (e) => {
    if (shouldCallLearnAction && useLearnActionClick) {
      backendService.experience.v4.clickAction({
        actionOrigin: actionOrigin || '',
        userOrigin: {
          playSource: playSource || '',
          userLogin: userType || '',
          msoProvider: msoProvider || '',
        },
        contentType: isLive ? 1 : 2,
        vrioAssetId: assetId || '',
        guideId: guideId || assetId || '',
      });
    }

    const avoidPlay = onClick ? onClick(e) || false : false;

    dispatch(updatePlaySource(PLAY_SOURCE_PAGE.PLAYER || ''));
    dispatch(updateActionOrigin(actionOrigin || ''));

    if (isMobile && isUserLoggedIn && !avoidPlay) {
      if (redirectToDetailsFlag || shouldDisplayLockIcon) {
        const interactionWithSearch = showSearch
          && (indexOfL(INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_LIVE, type) !== -1
            || indexOfL(INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_VOD, type) !== -1);
        if (interactionWithSearch) {
          setSearchInteraction({ searchTerm, assetId, action: INTERACTION_PDP });
        }
        redirectToPdp({
          assetId: isLive ? channelId : assetId,
          type,
          startTime,
          history,
          isLive,
          source: sourceToUse,
        });
      } else {
        redirectToApp(storeCode);
      }
      return;
    }

    if (!isUserLoggedIn && (mainAssetId || channelId) && !avoidPlay) {
      redirectToLoginPage(storeCode);
      return;
    }

    if (isUserLoggedIn && allowPlay && !avoidPlay && !isMobile) {
      const interactionWithSearch = showSearch
        && (indexOfL(INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_LIVE, type) !== -1
          || indexOfL(INTERACTIONS_TO_REGISTER_IN_THE_DATALAYER_FOR_VOD, type) !== -1);
      if (redirectToDetailsFlag || shouldDisplayLockIcon) {
        if (interactionWithSearch) {
          setSearchInteraction({ searchTerm, assetId, action: INTERACTION_PDP });
        }
        redirectToPdp({
          assetId: isLive ? channelId : assetId,
          type,
          startTime,
          history,
          isLive,
          source: sourceToUse,
        });
      } else {
        if (interactionWithSearch) {
          setSearchInteraction({ searchTerm, assetId, action: INTERACTION_PLAY });
        }
        if (isChromecastConnected) {
          const landerUrl = localStorage.getItem(LANDER_URL) ?? null;

          loadMedia({
            vmsId,
            assetId: isLive ? channelId : assetId,
            mainAssetId: mainAssetId || '',
            assetType: type,
            isLive,
            actionOrigin: actionOrigin || '',
            userOrigin: {
              playSource: PLAY_SOURCE_PAGE.PLAYER || '',
              userLogin: userType || '',
              msoProvider: msoProvider || '',
            },
          });

          const pathValues = Object.values(PATHS);
          const includesAnyPath = pathValues.some((pathVal) => location.pathname.includes(pathVal));

          if (!includesAnyPath) {
            if (landerUrl) {
              history.replace(landerUrl);
            } else {
              history.replace(PATHS.HOME);
            }
          }
        } else {
          if (path) setHistoryPathName({ pathname: path });
          const { redirect } = redirectToPlayer({
            fromEpg,
            fromEpgFilterId,
            fromEpgRedirectionIndex,
            history,
            assetId,
            replace,
            type,
            channelId,
            isLive,
            source: sourceToUse,
          });
          if (redirect) redirect();
        }
      }
    }
  };

  const Tag = isSpan ? 'span' : 'div';
  return (
    <Tag
      style={style}
      className={className}
      onClick={handleClick}
    >
      {children}
    </Tag>
  );
}

AssetToPlay.propTypes = {
  asset: PropTypes.shape({
    mainAssetId: PropTypes.string,
    assetId: PropTypes.string,
    type: PropTypes.string,
    channelId: PropTypes.string,
    shouldDisplayLockIcon: PropTypes.bool,
  }),
  style: PropTypes.shape(),
  className: PropTypes.string,
  isSpan: PropTypes.bool,
  allowPlay: PropTypes.bool,
  redirectToDetailsFlag: PropTypes.bool,
  onClick: PropTypes.func,
  shouldCallLearnAction: PropTypes.bool,
  fromEpgFilterId: PropTypes.number,
  fromEpgRedirectionIndex: PropTypes.number,
  fromEpg: PropTypes.bool,
  isLive: PropTypes.bool,
  replace: PropTypes.bool,
};

AssetToPlay.defaultProps = {
  asset: {
    type: '',
  },
  style: {},
  className: '',
  isSpan: false,
  allowPlay: true,
  redirectToDetailsFlag: false,
  onClick: () => {},
  shouldCallLearnAction: false,
  fromEpgFilterId: null,
  fromEpgRedirectionIndex: null,
  fromEpg: false,
  isLive: false,
  replace: false,
};

function mapStateToProps({
  search: { searchTerm, showSearch } = {},
  user,
  global,
}) {
  const account = getL(user, 'account', {});
  const { userCountry } = getL(global, 'configurationAEM.data', '');
  let isUserLoggedIn = false;

  if (!isEmptyL(account)) {
    isUserLoggedIn = account.isTVE || (!account.isTVE && account.uid);
  }

  return {
    isUserLoggedIn,
    searchTerm,
    showSearch,
    userCountry,
  };
}

export default connect(mapStateToProps, {
  setHistoryPathName: setHistoryPathNameAction,
  updatePlaySource: updatePlaySourceAction,
  setSearchInteraction: setSearchInteractionAction,
})(AssetToPlay);
